import React, { memo } from 'react';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';

/** active 步骤圆圈呼吸动画样式 */
const breatheKeyframes = `
@keyframes stepBreathe {
  0%, 100% { box-shadow: 0 0 0 4px rgba(32,148,240,0.15); }
  50% { box-shadow: 0 0 0 8px rgba(32,148,240,0.25); }
}
`;

/** 步骤状态 */
export type StepStatus = 'pending' | 'active' | 'completed';

interface StepItem {
  label: string;
  status: StepStatus;
}

interface Props {
  steps: StepItem[];
  /** 页面暂停时，active 步骤保持高亮但停止转圈动画。 */
  isPaused?: boolean;
}

/**
 * 水平步骤时间轴组件
 * - pending：灰色空心圆 + 灰色文字
 * - active：蓝色实心圆 + 转圈动画 + 蓝色文字（暂停时停止转圈）
 * - completed：绿色实心圆 + 白色打勾 + 绿色文字
 * - 连接线：前一步骤完成则绿色，否则灰色
 */
const StepTimeline: React.FC<Props> = ({ steps, isPaused = false }) => {
  return (
    <>
      <style>{breatheKeyframes}</style>
      <div className="flex items-start justify-center">
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            {/* 单个步骤：圆圈 + 标签 */}
            <div className="flex flex-col items-center" style={{ width: 160 }}>
              {/* 圆圈 */}
              <div
                className={[
                  'flex items-center justify-center rounded-full transition-all duration-500 ease-in-out',
                  step.status === 'completed'
                    ? 'h-9 w-9 bg-[#22c55e] text-white shadow-[0_0_0_4px_rgba(34,197,94,0.15)]'
                    : step.status === 'active'
                      ? 'h-11 w-11 bg-[#2094f0] text-white'
                      : 'h-9 w-9 bg-[#d1d5db] text-white',
                ].join(' ')}
                style={
                  step.status === 'active' && !isPaused
                    ? { animation: 'stepBreathe 2s ease-in-out infinite' }
                    : undefined
                }
              >
                {step.status === 'completed' ? (
                  <CheckOutlined style={{ fontSize: 16 }} />
                ) : step.status === 'active' && !isPaused ? (
                  <LoadingOutlined style={{ fontSize: 20 }} />
                ) : (
                  <span className="text-sm font-semibold">{index + 1}</span>
                )}
              </div>
              {/* 标签文字 */}
              <span
                className={[
                  'mt-2.5 text-center text-xs font-medium leading-tight transition-colors duration-500',
                  step.status === 'completed'
                    ? 'text-[#22c55e]'
                    : step.status === 'active'
                      ? 'text-[#2094f0]'
                      : 'text-[#9ca3af]',
                ].join(' ')}
              >
                {step.label}
              </span>
            </div>

            {/* 连接线：前一步骤完成则绿色 */}
            {index < steps.length - 1 && (
              <div
                className="-mx-4 flex flex-1 items-center"
                style={{ height: 36 }}
              >
                <div
                  className={[
                    'h-[3px] w-full rounded-full transition-all duration-700 ease-in-out',
                    steps[index].status === 'completed'
                      ? 'bg-[#22c55e]'
                      : 'bg-[#e5e7eb]',
                  ].join(' ')}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
};

export default memo(StepTimeline);
